import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { EnvironmentOutlined } from '@ant-design/icons';
import { ROUTES, GENDERS, SELECTABLE_GRADE_OPTIONS } from '@utils/constants';
import { searchCampuses } from '@api/students';
import { searchShifts } from '@api/shifts';
import { saveStudentSession, getStudentSession } from '@utils/sessionStorage';
import { useToastContext } from '../../context/ToastContext';
import { useCampusFilter } from '../../context/CampusContext';
import { buildCampusSearchQuery } from '../../utils/campusFilter';
import { AppHeader } from '@components/layout/AppHeader';
import { InfoCard } from '@components/ui/InfoCard';
import { filterShiftsByGrade } from '@utils/shiftFilter';
import './styles/studentdetails.css';
import '@styles/loader.css';
import { ShiftDetails } from '@dev-zeromoblt/o-platform-client';

interface CampusOption {
  id: string;
  name: string;
  location?: any;
  contact?: {
    address?: {
      primaryAddressLine?: string;
      secondaryAddressLine?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

interface StudentFormData {
  givenName: string;
  familyName: string;
  gender: string;
  gradeValue: string;
  campusIdValue: string;
  shiftIdValue: string;
}

export const StudentDetails: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const { campusIds, campusSlug } = useCampusFilter();
  const [loading, setLoading] = useState(false);
  const [campusesLoading, setCampusesLoading] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  
  const [formData, setFormData] = useState<StudentFormData>({
    givenName: '',
    familyName: '',
    gender: '',
    gradeValue: '',
    campusIdValue: '',
    shiftIdValue: '',
  });
  
  const [errors, setErrors] = useState<Partial<StudentFormData>>({});
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [shifts, setShifts] = useState<ShiftDetails[]>([]);

  // Initialize: fetch campuses and restore session data if available
  useEffect(() => {
    const initializeForm = async () => {
      const session = getStudentSession();
      
      // Fetch campuses first
      try {
        setCampusesLoading(true);
        if(session?.formData) setFormData({
              givenName: session.formData.givenName,
              familyName: session.formData.familyName,
              gender: session.formData.gender,
              gradeValue: typeof session.formData.grade === 'string' ? session.formData.grade : session.formData.grade?.value || '',
              campusIdValue: session.formData.campus?.id || '',
              shiftIdValue: session.formData.shift?.id || '',
            });
 
        const response = await searchCampuses(buildCampusSearchQuery(campusIds));
        if (response.campuses) {
          const allValid = response.campuses
            .filter(c => c.id && c.name && c.status !== 'UNVERIFIED')
            .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

          let finalCampuses = allValid;

          // If a campus slug is active (e.g. from /allen), filter by name match
          if (campusSlug && !campusIds) {
            const slugFiltered = allValid.filter(c =>
              (c.name || '').toLowerCase().includes(campusSlug.toLowerCase())
            );
            // Fallback: if slug matched nothing, show all campuses
            if (slugFiltered.length > 0) {
              finalCampuses = slugFiltered;
            }
          }

          // Fallback: if zid filter returned no results, fetch all campuses
          if (finalCampuses.length === 0 && campusIds && campusIds.length > 0) {
            const fallbackResponse = await searchCampuses('*');
            if (fallbackResponse.campuses) {
              finalCampuses = fallbackResponse.campuses
                .filter(c => c.id && c.name && c.status !== 'UNVERIFIED')
                .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
            }
          }

          setCampuses(finalCampuses as CampusOption[]);

          // If session data exists, restore it and fetch shifts
          if (session?.formData) {
            // Fetch shifts for the stored campus
            const campusId = session.formData.campus?.id;
            if (campusId) {
              try {
                setShiftsLoading(true);
                const shiftsResponse = await searchShifts(campusId);
                if (shiftsResponse.shifts && shiftsResponse.shifts.length > 0) {
                  setShifts(shiftsResponse.shifts);
                }
              } catch (error) {
                console.error('Error fetching shifts:', error);
              } finally {
                setShiftsLoading(false);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching campuses:', error);
        addToast({
          type: 'error',
          title: 'Failed to Load Campuses',
          message: 'Unable to load campuses. Please try again.',
        });
      } finally {
        setCampusesLoading(false);
      }
    };

    initializeForm();
  }, []);

  // Fetch shifts when campus changes
  useEffect(() => {
    if (formData.campusIdValue) {
      fetchShifts(formData.campusIdValue);
    } else {
      setShifts([]);
      setFormData(prev => ({ ...prev, shiftIdValue: '' }));
    }
  }, [formData.campusIdValue]);

  // Filter shifts based on grade and handle auto-selection
  const filteredShifts = useMemo(() => {
    return filterShiftsByGrade(shifts, formData.gradeValue);
  }, [shifts, formData.gradeValue]);

  // Handle auto-selection and clearing when grade or shifts change
  useEffect(() => {
    // If no grade selected, clear shift selection
    if (!formData.gradeValue) {
      setFormData(prev => ({ ...prev, shiftIdValue: '' }));
      return;
    }

    // If no shifts available, don't do anything
    if (shifts.length === 0) return;

    const filtered = filterShiftsByGrade(shifts, formData.gradeValue);

    // If only 1 shift after filtering, auto-select it
    if (filtered.length === 1) {
      setFormData(prev => ({ ...prev, shiftIdValue: filtered[0].id || '' }));
    } else if (filtered.length > 1) {
      // If multiple shifts after filtering, check if current selection is still valid
      const isCurrentShiftValid = filtered.some(s => s.id === formData.shiftIdValue);
      if (!isCurrentShiftValid) {
        // Clear shift if currently selected shift is no longer in filtered list
        setFormData(prev => ({ ...prev, shiftIdValue: '' }));
      }
    } else {
      // No shifts match the grade - clear selection
      setFormData(prev => ({ ...prev, shiftIdValue: '' }));
    }
  }, [formData.gradeValue, shifts]);

  // Clear shift validation error when shift gets auto-selected
  useEffect(() => {
    if (formData.shiftIdValue && errors.shiftIdValue) {
      setErrors(prev => ({ ...prev, shiftIdValue: undefined }));
    }
  }, [formData.shiftIdValue]);

  const fetchShifts = async (campusId: string) => {
    try {
      setShiftsLoading(true);
      const response = await searchShifts(campusId);
      if (response.shifts && response.shifts.length > 0) {
        setShifts(response.shifts);
        
        // If grade is already selected, filter shifts and auto-select
        const gradeValue = formData.gradeValue;
        if (gradeValue) {
          const filtered = filterShiftsByGrade(response.shifts, gradeValue);
          if (filtered.length === 1) {
            setFormData(prev => ({ ...prev, shiftIdValue: filtered[0].id || '' }));
          } else {
            setFormData(prev => ({ ...prev, shiftIdValue: '' }));
          }
        } else {
          // No grade selected - use original logic
          if (response?.shifts?.length === 1) {
            setFormData(prev => ({ ...prev, shiftIdValue: response.shifts![0].id || '' }));
          } else {
            setFormData(prev => ({ ...prev, shiftIdValue: '' }));
          }
        }
      } else {
        setShifts([]);
        setFormData(prev => ({ ...prev, shiftIdValue: '' }));
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      addToast({
        type: 'error',
        title: 'Failed to Load Shifts',
        message: 'Unable to load shifts for this campus. Please try again.',
      });
    } finally {
      setShiftsLoading(false);
    }
  };

  const handleInputChange = (field: keyof StudentFormData, value: any) => {
    let processedValue = value;
    
    if (field === 'givenName' || field === 'familyName') {
      processedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    
    // Clear shift when campus changes - new shifts will be fetched via useEffect
    if (field === 'campusIdValue' && value !== formData.campusIdValue) {
      setFormData({ 
        ...formData, 
        [field]: processedValue,
        shiftIdValue: '' // Clear existing shift when campus changes
      });
    } else {
      setFormData({ ...formData, [field]: processedValue });
    }
    
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '48px',
      borderColor: '#e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '10px',
      fontSize: '14px',
      cursor: 'pointer',
      '&:hover': { borderColor: '#f5a623' },
    }),
    option: (base: any, optionState: any) => ({
      ...base,
      backgroundColor: optionState.isSelected ? '#f5a623' : optionState.isFocused ? '#fffbeb' : '#ffffff',
      color: '#111827',
      cursor: 'pointer',
      padding: '12px 16px',
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '10px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    }),
  };

  // Refs for scroll-to-error
  const givenNameRef = React.useRef<HTMLDivElement>(null);
  const familyNameRef = React.useRef<HTMLDivElement>(null);
  const genderRef = React.useRef<HTMLDivElement>(null);
  const gradeRef = React.useRef<HTMLDivElement>(null);
  const campusRef = React.useRef<HTMLDivElement>(null);
  const shiftRef = React.useRef<HTMLDivElement>(null);

  const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    givenName: givenNameRef,
    familyName: familyNameRef,
    gender: genderRef,
    gradeValue: gradeRef,
    campusIdValue: campusRef,
    shiftIdValue: shiftRef,
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<StudentFormData> = {};

    if (!formData.givenName.trim()) {
      newErrors.givenName = 'Please enter first name';
    }
    if (!formData.familyName.trim()) {
      newErrors.familyName = 'Please enter last name';
    }
    if (!formData.gender) newErrors.gender = 'Please select gender';
    if (!formData.gradeValue) newErrors.gradeValue = 'Please select grade';
    if (!formData.campusIdValue) newErrors.campusIdValue = 'Please select campus';
    if (!formData.shiftIdValue) newErrors.shiftIdValue = 'Please select shift';

    setErrors(newErrors);

    // Scroll to the first error field
    const errorKeys = ['givenName', 'familyName', 'gender', 'gradeValue', 'campusIdValue', 'shiftIdValue'];
    for (const key of errorKeys) {
      if (newErrors[key as keyof StudentFormData]) {
        fieldRefs[key]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const selectedGrade = SELECTABLE_GRADE_OPTIONS.find(g => g.value === formData.gradeValue);
      const selectedCampus = campuses.find(c => c.id === formData.campusIdValue);
      const selectedShift = shifts.find(s => s.id === formData.shiftIdValue);

      // Get existing session to preserve isNewStudent flag
      const existingSession = getStudentSession();

      const newFormData = {
        givenName: formData.givenName,
        familyName: formData.familyName,
        gender: formData.gender,
        grade: selectedGrade || { value: formData.gradeValue, label: formData.gradeValue },
        campus: {
          id: selectedCampus?.id || formData.campusIdValue,
          name: selectedCampus?.name || '',
          location: selectedCampus?.location || undefined,
          contact: selectedCampus?.contact,
        },
        shift: selectedShift && selectedShift.id ? { id: selectedShift.id, name: selectedShift.name || '', displayLabel: selectedShift.displayLabel } : { id: formData.shiftIdValue, name: '' },
        geoAddress: '',
      };

      // Preserve existing session data (pickupLocation, startDate, transportType, etc.)
      // and preserve isNewStudent flag from existing session
      saveStudentSession({
        ...existingSession,
        formData: newFormData,
        isNewStudent: existingSession?.isNewStudent ?? true, // Preserve existing flag or default to true
      });

      navigate(ROUTES.PICKUP_LOCATION, {
        state: {
          formData: {
            givenName: formData.givenName,
            familyName: formData.familyName,
            gender: formData.gender,
            grade: selectedGrade,
            campus: selectedCampus,
            shift: selectedShift,
          },
          campuses,
          shifts,
        },
      });
    } catch (error) {
      console.error('Error navigating:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGenderIcon = (gender: string) => {
    switch (gender) {
      case 'M':
        return <span style={{ fontSize: '20px', color: '#2196F3' }}>♂</span>;
      case 'F':
        return <span style={{ fontSize: '20px', color: '#E91E63' }}>♀</span>;
      default:
        return <span style={{ fontSize: '20px', color: '#9C27B0' }}>◯</span>;
    }
  };

  return (
    <div className="student-details-page">
      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <div className="student-details-page__container">
        <div className="student-details-page__content">
          {/* Title Section */}
          <div className="student-details-page__title-section">
            <h1 className="student-details-page__title">Tell us about<br />your child</h1>
            <p className="student-details-page__subtitle">
              This helps us assign the fastest and safest route
            </p>
          </div>

          {/* Form */}
          <form className="student-details-page__form">
            {/* Student Name */}
            <div className="student-details-page__form-group" ref={givenNameRef}>
              <div className="student-details-page__card">
                <label className="student-details-page__card-label">STUDENT NAME</label>
                <div className="student-details-page__name-row">
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      placeholder="First name"
                      value={formData.givenName}
                      onChange={(e) => handleInputChange('givenName', e.target.value)}
                      className={`student-details-page__input ${errors.givenName ? 'error' : ''}`}
                    />
                    {errors.givenName && (
                      <span className="student-details-page__error">{errors.givenName}</span>
                    )}
                  </div>
                  <div ref={familyNameRef} style={{ flex: 1 }}>
                    <input
                      type="text"
                      placeholder="Last name"
                      value={formData.familyName}
                      onChange={(e) => handleInputChange('familyName', e.target.value)}
                      className={`student-details-page__input ${errors.familyName ? 'error' : ''}`}
                    />
                    {errors.familyName && (
                      <span className="student-details-page__error">{errors.familyName}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Gender */}
            <div className="student-details-page__form-group" ref={genderRef}>
              <div className="student-details-page__card student-details-page__gender-card">
                <label className="student-details-page__card-label">GENDER</label>
                <div className="student-details-page__gender-group">
                  {GENDERS.map(g => (
                    <button
                      key={g.value}
                      type="button"
                      className={`student-details-page__gender-btn ${
                        formData.gender === g.value ? 'active' : ''
                      }`}
                      onClick={() => handleInputChange('gender', g.value)}
                    >
                      <span className="student-details-page__gender-icon">
                        {getGenderIcon(g.value)}
                      </span>
                      <span className="student-details-page__gender-label">{g.label}</span>
                    </button>
                  ))}
                </div>
                {errors.gender && (
                  <span className="student-details-page__error">{errors.gender}</span>
                )}
              </div>
            </div>

            {/* Grade */}
            <div className="student-details-page__form-group" ref={gradeRef}>
              <div className="student-details-page__card">
                <label className="student-details-page__card-label">GRADE/CLASS</label>
                <Select
                  options={SELECTABLE_GRADE_OPTIONS}
                  value={SELECTABLE_GRADE_OPTIONS.find(g => g.value === formData.gradeValue) || null}
                  onChange={(option) => handleInputChange('gradeValue', option?.value || '')}
                  placeholder="Select grade"
                  isDisabled={false}
                  isSearchable={false}
                  classNamePrefix="student-select"
                  styles={selectStyles}
                />
                {errors.gradeValue && (
                  <span className="student-details-page__error">{errors.gradeValue}</span>
                )}
              </div>
            </div>

            {/* Campus */}
            <div className="student-details-page__form-group" ref={campusRef}>
              <div className="student-details-page__card">
                <label className="student-details-page__card-label">CAMPUS</label>
                <Select
                  options={campuses.map(c => ({ value: c.id, label: c.name }))}
                  value={campuses.find(c => c.id === formData.campusIdValue) ? { value: formData.campusIdValue, label: campuses.find(c => c.id === formData.campusIdValue)?.name } : null}
                  onChange={(option) => handleInputChange('campusIdValue', option?.value || '')}
                  placeholder="Select campus"
                  isDisabled={campusesLoading}
                  isLoading={campusesLoading}
                  classNamePrefix="student-select"
                  styles={selectStyles}
                />
                {errors.campusIdValue && (
                  <span className="student-details-page__error">{errors.campusIdValue}</span>
                )}
              </div>
            </div>

            {/* Shift */}
            <div className="student-details-page__form-group" ref={shiftRef}>
              <div className="student-details-page__card">
                <label className="student-details-page__card-label">SCHOOL SHIFT</label>
                <Select
                  options={filteredShifts.map(s => ({ value: s.id || '', label: s.displayLabel || s.name }))}
                  value={
                    filteredShifts.find(s => s.id === formData.shiftIdValue)
                      ? { value: formData.shiftIdValue, label: filteredShifts.find(s => s.id === formData.shiftIdValue)?.displayLabel || filteredShifts.find(s => s.id === formData.shiftIdValue)?.name }
                      : null
                  }
                  onChange={(option) => handleInputChange('shiftIdValue', option?.value || '')}
                  placeholder="Select shift"
                  isDisabled={shiftsLoading || !formData.campusIdValue || filteredShifts.length === 1}
                  isLoading={shiftsLoading}
                  isSearchable={false}
                  classNamePrefix="student-select"
                  styles={selectStyles}
                />
                {errors.shiftIdValue && (
                  <span className="student-details-page__error">{errors.shiftIdValue}</span>
                )}
              </div>
            </div>

            {/* Info Card */}
            <InfoCard
              icon={<EnvironmentOutlined style={{ fontSize: '24px', color: '#FFD700' }} />}
              title="SMART ROUTING"
              description="Our AI optimizes pickup times and routes based on your child's grade and campus location."
              className="student-details-info-card"
            />

            {/* Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`student-details-page__button ${loading ? 'btn-loading' : ''}`}
            >
              {loading ? (
                <>
                  <span className="btn-loader btn-loader--dark"></span>
                  Loading...
                </>
              ) : (
                'Continue →'
              )}
            </button>

            {/* Loading Modal Overlay */}
            {loading && (
              <div className="student-details-page__modal-overlay">
                <div className="student-details-page__modal">
                  <span className="btn-loader btn-loader--dark" style={{ width: 40, height: 40 }}></span>
                  <h3>Processing</h3>
                  <p>Please wait while we save your details...</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
